import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
    FileText, Upload, Download, Trash2, Eye, Loader2, Plus, Receipt, ClipboardList, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { chantierDocsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import PdfAllPagesViewer from '@/components/PdfAllPagesViewer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const CATEGORY_META = {
    etude: {
        label: 'Études',
        singular: 'étude',
        Icon: ClipboardList,
        accent: 'indigo',
        desc: "Téléversez ici les études (PDF jusqu'à 50 pages, images). Plusieurs études possibles pour les multi-chantiers. Consultables et téléchargeables par tous ceux qui ont accès au dossier.",
    },
    devis: {
        label: 'Devis',
        singular: 'devis',
        Icon: Receipt,
        accent: 'emerald',
        desc: "Téléversez ici les devis (PDF jusqu'à 50 pages, images). Plusieurs devis possibles pour les multi-chantiers. Consultables et téléchargeables par tous ceux qui ont accès au dossier.",
    },
};

const ACCENT = {
    indigo: { border: 'border-indigo-200', grad: 'from-indigo-50/60 via-white to-sky-50/40', icon: 'text-indigo-600', chip: 'bg-indigo-100 text-indigo-700', btn: 'bg-indigo-600 hover:bg-indigo-700', soft: 'bg-indigo-100 text-indigo-600', ring: 'border-indigo-300 hover:bg-indigo-50/30' },
    emerald: { border: 'border-emerald-200', grad: 'from-emerald-50/60 via-white to-teal-50/40', icon: 'text-emerald-600', chip: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700', soft: 'bg-emerald-100 text-emerald-600', ring: 'border-emerald-300 hover:bg-emerald-50/30' },
};

function formatFileSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
}

export default function ChantierDocsSection({ prospectId, category, readOnly = false }) {
    const { user } = useAuth();
    const meta = CATEGORY_META[category];
    const a = ACCENT[meta.accent];
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isParrain = user?.role === 'parrain';
    const canUpload = !readOnly && !isParrain;

    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [docName, setDocName] = useState('');
    const [docFile, setDocFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [viewingId, setViewingId] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputId = `chantier-doc-file-${category}`;

    const load = async () => {
        setLoading(true);
        try {
            const res = await chantierDocsAPI.list(prospectId, category);
            setDocs(Array.isArray(res.data) ? res.data : []);
        } catch {
            /* silencieux : non bloquant */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [prospectId, category]);

    const handleUpload = async () => {
        if (!docName.trim() || !docFile) {
            toast.error('Nom et fichier sont obligatoires');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', docFile);
            fd.append('category', category);
            fd.append('name', docName.trim());
            await chantierDocsAPI.create(prospectId, fd);
            toast.success(`${meta.singular.charAt(0).toUpperCase() + meta.singular.slice(1)} ajouté${category === 'etude' ? 'e' : ''}`);
            setDocName('');
            setDocFile(null);
            const input = document.getElementById(fileInputId);
            if (input) input.value = '';
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'ajout");
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (doc) => {
        setDownloadingId(doc.id);
        try {
            const res = await chantierDocsAPI.download(doc.id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.original_filename || doc.name || 'document';
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleView = async (doc) => {
        setViewingId(doc.id);
        try {
            const res = await chantierDocsAPI.download(doc.id);
            const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            setPreviewDoc(doc);
            setPreviewUrl(url);
        } catch {
            toast.error("Erreur lors de l'ouverture du document");
        } finally {
            setViewingId(null);
        }
    };

    const closePreview = () => {
        if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewDoc(null);
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Supprimer « ${doc.name} » ?`)) return;
        try {
            await chantierDocsAPI.remove(doc.id);
            toast.success('Document supprimé');
            setDocs((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const { Icon } = meta;

    return (
        <Card className={`${a.border} bg-gradient-to-br ${a.grad}`} data-testid={`chantier-docs-${category}`}>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-slate-900">
                    <Icon className={`w-5 h-5 ${a.icon}`} />
                    {meta.label}
                    {docs.length > 0 && (
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${a.chip} font-medium`}>{docs.length}</span>
                    )}
                </CardTitle>
                <p className="text-xs text-slate-500">{meta.desc}</p>
            </CardHeader>
            <CardContent>
                {/* Formulaire d'ajout */}
                {canUpload && (
                    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3" data-testid={`chantier-doc-upload-${category}`}>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                                placeholder={`Nom du ${meta.singular} (ex : Travaux d’optimisation bâtiment A) *`}
                                value={docName}
                                onChange={(e) => setDocName(e.target.value)}
                                className="sm:flex-1"
                                data-testid={`chantier-doc-name-${category}`}
                            />
                            <input
                                id={fileInputId}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                                className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-slate-700 file:font-medium"
                                data-testid={`chantier-doc-file-input-${category}`}
                            />
                            <Button
                                onClick={handleUpload}
                                disabled={uploading}
                                className={`gap-1.5 ${a.btn} sm:ml-auto`}
                                data-testid={`chantier-doc-submit-${category}`}
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Ajouter
                            </Button>
                        </div>
                    </div>
                )}

                {/* Liste */}
                {loading ? (
                    <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                    </div>
                ) : docs.length === 0 ? (
                    <div className="py-6 text-center text-slate-500" data-testid={`chantier-docs-empty-${category}`}>
                        <FileText className="w-9 h-9 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm">Aucun{category === 'etude' ? 'e' : ''} {meta.singular} pour ce dossier.</p>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid={`chantier-docs-list-${category}`}>
                        {docs.map((doc) => (
                            <div
                                key={doc.id}
                                className={`flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:${a.ring}`}
                                data-testid={`chantier-doc-${doc.id}`}
                            >
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${a.soft}`}>
                                    <FileText className="w-4.5 h-4.5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-slate-800 truncate">{doc.name}</p>
                                    <p className="text-[11px] text-slate-400 truncate">
                                        {doc.original_filename} · {formatFileSize(doc.file_size)}
                                    </p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                        {doc.created_by_name} · {doc.created_at ? format(new Date(doc.created_at), 'dd/MM/yy HH:mm', { locale: fr }) : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                        onClick={() => handleView(doc)}
                                        disabled={viewingId === doc.id}
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        title="Visionner"
                                        data-testid={`chantier-doc-view-${doc.id}`}
                                    >
                                        {viewingId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                    </Button>
                                    <Button
                                        onClick={() => handleDownload(doc)}
                                        disabled={downloadingId === doc.id}
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        title="Télécharger"
                                        data-testid={`chantier-doc-download-${doc.id}`}
                                    >
                                        {downloadingId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                    </Button>
                                    {isAdmin && (
                                        <Button
                                            onClick={() => handleDelete(doc)}
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                            title="Supprimer"
                                            data-testid={`chantier-doc-delete-${doc.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>

            {/* Modale de prévisualisation — plein écran mobile, bouton Retour toujours visible */}
            <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) closePreview(); }}>
                <DialogContent
                    className="max-w-6xl w-[98vw] sm:w-[95vw] p-0 overflow-hidden rounded-none sm:rounded-xl h-[100dvh] sm:h-[92vh] sm:max-h-[92vh] flex flex-col"
                    data-testid={`chantier-doc-preview-modal-${category}`}
                >
                    <DialogHeader className="px-3 sm:px-5 py-3 border-b bg-white flex-row items-center gap-3 space-y-0 shrink-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={closePreview}
                            className="shrink-0 gap-1 -ml-2"
                            data-testid={`chantier-doc-preview-back-${category}`}
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
                                <FileText className={`w-4 h-4 sm:w-5 sm:h-5 ${a.icon} shrink-0`} />
                                <span className="truncate">{previewDoc?.name || 'Document'}</span>
                            </DialogTitle>
                            <DialogDescription className="truncate text-xs">
                                {previewDoc?.original_filename}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    <div className="bg-slate-100 flex-1 min-h-0">
                        {previewUrl && previewDoc && (
                            (previewDoc.mime_type || '').startsWith('image/') ? (
                                <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
                                    <img
                                        src={previewUrl}
                                        alt={previewDoc.name}
                                        className="max-w-full max-h-full object-contain mx-auto"
                                        data-testid={`chantier-doc-preview-image-${category}`}
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-full mx-auto">
                                    <PdfAllPagesViewer url={previewUrl} />
                                </div>
                            )
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-3 border-t bg-white shrink-0">
                        <Button variant="outline" size="sm" onClick={closePreview} className="gap-1 sm:hidden">
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </Button>
                        <div className="hidden sm:block" />
                        {previewDoc && (
                            <Button onClick={() => handleDownload(previewDoc)} className={`gap-2 ${a.btn}`} data-testid={`chantier-doc-preview-download-${category}`}>
                                <Download className="w-4 h-4" /> Télécharger
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
